/**
 * Bank CSV format registry.
 *
 * Each format knows how to turn a raw parsed CSV row into the transaction
 * shape the app stores. Add a new bank by appending an entry here — the
 * Upload page's dropdown is driven off this object.
 */

// --- shared helpers ---------------------------------------------------------

export function parseCSVLine(line) {
  const result = [];
  let cur = '', inQ = false;
  for (const ch of line) {
    if (ch === '"') inQ = !inQ;
    else if (ch === ',' && !inQ) { result.push(cur); cur = ''; }
    else cur += ch;
  }
  result.push(cur);
  return result;
}

/** Split raw CSV text into an array of string arrays. */
export function splitRows(text) {
  return text.trim().split(/\r?\n/).filter(l => l.trim()).map(parseCSVLine);
}

/** DD/MM/YYYY (Australian) or YYYY-MM-DD -> YYYY-MM-DD */
function parseDate(str) {
  if (!str) return '';
  const s = str.trim().replace(/^"|"$/g, '');
  const parts = s.split(/[/\-.]/);
  if (parts.length !== 3) return '';
  const [a, b, c] = parts;
  if (a.length === 4) return `${a}-${b.padStart(2, '0')}-${c.padStart(2, '0')}`;
  return `${c}-${b.padStart(2, '0')}-${a.padStart(2, '0')}`;
}

function toNumber(str) {
  if (str == null || str === '') return 0;
  return parseFloat(String(str).replace(/[^0-9.\-]/g, '')) || 0;
}

function deriveYearMonth(isoDate) {
  const d = new Date(isoDate);
  if (isNaN(d.getTime())) return { year: 0, month: 0 };
  return { year: d.getFullYear(), month: d.getMonth() + 1 };
}

function findCol(headerRow, ...patterns) {
  const lower = headerRow.map(h => h.trim().replace(/"/g, '').toLowerCase());
  for (const p of patterns) {
    const idx = lower.findIndex(h => h.includes(p));
    if (idx !== -1) return idx;
  }
  return -1;
}

// --- format definitions -----------------------------------------------------

export const BANK_FORMATS = {
  ing: {
    id: 'ing',
    label: 'ING — Orange Everyday / Savings',
    hasHeader: true,
    hint: 'Columns: Date, Description, Credit, Debit, Balance',
    parse(rows) {
      const header = rows[0];
      const iDate = findCol(header, 'date');
      const iDesc = findCol(header, 'description', 'narrative', 'detail');
      const iCredit = findCol(header, 'credit');
      const iDebit = findCol(header, 'debit');
      const iBalance = findCol(header, 'balance');

      return rows.slice(1).map(r => {
        const date = parseDate(r[iDate]);
        const rawCredit = iCredit >= 0 ? toNumber(r[iCredit]) : 0;
        const rawDebit = iDebit >= 0 ? toNumber(r[iDebit]) : 0;

        // ING writes debits as negative numbers in the Debit column.
        let amount, type;
        if (rawDebit !== 0) {
          amount = Math.abs(rawDebit);
          type = rawDebit < 0 ? 'debit' : 'credit';
        } else {
          amount = Math.abs(rawCredit);
          type = rawCredit >= 0 ? 'credit' : 'debit';
        }

        return {
          date,
          description: (r[iDesc] || '').trim() || 'Unknown',
          type,
          amount,
          balance: iBalance >= 0 ? toNumber(r[iBalance]) : null,
          category: '',
          ...deriveYearMonth(date),
        };
      }).filter(t => t.date && t.amount > 0);
    },
  },

  anz: {
    id: 'anz',
    label: 'ANZ — Credit Card (no header row)',
    hasHeader: false,
    hint: 'Column A = Date, Column B = Amount, Column C = Description',
    parse(rows) {
      // Headerless: A=date, B=amount, C=description.
      // Guard against a stray header row if the export ever gains one.
      const start = /^\s*"?date"?\s*$/i.test(rows[0]?.[0] || '') ? 1 : 0;

      return rows.slice(start).map(r => {
        const date = parseDate(r[0]);
        const raw = toNumber(r[1]);
        // Credit card: negative = purchase (debit), positive = repayment (credit)
        const type = raw < 0 ? 'debit' : 'credit';
        const description = (r[2] || '').replace(/\s+/g, ' ').trim() || 'Unknown';

        // Card repayments always land in the same category — you confirmed this.
        const isRepayment = type === 'credit' && /payment\s*thank\s*you/i.test(description);

        return {
          date,
          description,
          type,
          amount: Math.abs(raw),
          balance: null,
          category: isRepayment ? 'Credit Card Repayments' : '',
          ...deriveYearMonth(date),
        };
      }).filter(t => t.date && t.amount > 0);
    },
  },

  generic: {
    id: 'generic',
    label: 'Generic — auto-detect columns from headers',
    hasHeader: true,
    hint: 'Looks for Date, Description, Credit/Debit or Amount, Balance, Category',
    parse(rows) {
      const header = rows[0];
      const iDate = findCol(header, 'date');
      const iDesc = findCol(header, 'description', 'desc', 'narrative', 'detail', 'memo', 'particulars', 'reference');
      const iCredit = findCol(header, 'credit');
      const iDebit = findCol(header, 'debit');
      const iAmount = findCol(header, 'amount');
      const iBalance = findCol(header, 'balance');
      const iCategory = findCol(header, 'category');

      return rows.slice(1).map(r => {
        const date = parseDate(r[iDate]);
        let amount, type;

        if (iDebit >= 0 || iCredit >= 0) {
          const rawDebit = iDebit >= 0 ? toNumber(r[iDebit]) : 0;
          const rawCredit = iCredit >= 0 ? toNumber(r[iCredit]) : 0;
          if (rawDebit !== 0) {
            amount = Math.abs(rawDebit);
            type = rawDebit < 0 ? 'debit' : 'credit';
          } else {
            amount = Math.abs(rawCredit);
            type = rawCredit >= 0 ? 'credit' : 'debit';
          }
        } else {
          const raw = iAmount >= 0 ? toNumber(r[iAmount]) : 0;
          amount = Math.abs(raw);
          type = raw < 0 ? 'debit' : 'credit';
        }

        return {
          date,
          description: (r[iDesc] || '').trim() || 'Unknown',
          type,
          amount,
          balance: iBalance >= 0 ? toNumber(r[iBalance]) : null,
          category: iCategory >= 0 ? (r[iCategory] || '').trim() : '',
          ...deriveYearMonth(date),
        };
      }).filter(t => t.date && t.amount > 0);
    },
  },
};

export const BANK_FORMAT_LIST = Object.values(BANK_FORMATS);
